import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardActions,
    Button,
    Avatar,
    Chip,
    Divider,
    useTheme,
    IconButton,
    Pagination,
    CircularProgress,
    Paper,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Menu,
    MenuItem,
    Badge,
    Alert,
    Tooltip,
    Popover,
    FormControl,
    InputLabel,
    TablePagination,
    FormGroup,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    Select
} from '@mui/material';
import {
    Campaign,
    CheckCircle,
    Cancel,
    HourglassEmpty,
    Gavel,
    AttachMoney,
    Visibility,
    Refresh,
    ArrowBack,
    ArrowForward,
    MoreVert,
    Block,
    Payment,
    AccountBalanceWallet,
    Search,
    FilterList,
    Warning,
    VerifiedUser,
    MoneyOff
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, subMonths } from 'date-fns';
import api from '../../../services/api';
import PreviewCampaignModal from '../../fundsCampaign/myCampaigns/PreviewCampaignDialog';
import PayoutModal from './PayoutModal';
import RefundModal from './RefundModal';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import VerifyCampaignModal from './VerifyCampaignModal';
import SuspendCampaignModal from './SuspendCampaignModal';


const AdminCampaignDashboard = () => {
    const theme = useTheme();
    // Filters & search
    const [statusFilter, setStatusFilter] = useState('all'); // all | ACTIVE | PENDING | SUSPENDED | COMPLETED | FLAGGED | DELETED
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0
    });
    const [searchBy, setSearchBy] = useState('title'); // title | createdBy | id
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState({ term: '', by: 'title' });
    const [anchorEl, setAnchorEl] = useState(null);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [payoutModalOpen, setPayoutModalOpen] = useState(false);
    const [refundModalOpen, setRefundModalOpen] = useState(false);
    const [verifyModalOpen, setVerifyModalOpen] = useState(false);
    const [suspendModalOpen, setSuspendModalOpen] = useState(false);
    const [isSuspending, setIsSuspending] = useState(true);
    const [actionSuccess, setActionSuccess] = useState(null);
    const [dateRange, setDateRange] = useState({
        start: subMonths(new Date(), 1),
        end: new Date()
    });
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [filters, setFilters] = useState({
        categories: [],
        general: [],
        minAmount: '',
        maxAmount: ''
    });

    // Status options for filter select
    const statusOptions = [
        { value: 'all', label: 'All Campaigns' },
        { value: 'ACTIVE', label: 'Active' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'SUSPENDED', label: 'Suspended' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'FLAGGED', label: 'Flagged' },
        { value: 'DELETED', label: 'Deleted' }
    ];

    const fetchCampaigns = async () => {
        setLoading(true);

        try {
            const params = {
                page: pagination.page,
                size: pagination.size,
                startDate: dateRange.start ? format(dateRange.start, 'yyyy-MM-dd') : undefined,
                endDate: dateRange.end ? format(dateRange.end, 'yyyy-MM-dd') : undefined,
                ...(filters.categories.length > 0 && { categories: filters.categories.join(',') }),
                ...(filters.minAmount && { minAmount: filters.minAmount }),
                ...(filters.maxAmount && { maxAmount: filters.maxAmount })
            };

            // Apply status filter
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            // Apply search
            if (appliedSearch.term) {
                params.search = appliedSearch.term;
                params.searchField = appliedSearch.by;
            }

            if (filters.general.length > 0) {
                const filteredGeneral = filters.general;

                if (filteredGeneral.includes('Verified')) {
                    params.verified = true;
                }
                if (filteredGeneral.includes('Pending Verification')) {
                    params.isVerified = false; // Assuming you want to set isVerified to false for Pending Verification
                    params.requiresVerification = true;
                }
                if (filteredGeneral.includes('Requires Verification')) {
                    params.requiresVerification = true;
                    params.verified = false; // Assuming you want to set verified to false for Requires Verification
                }
            }

            // Remove undefined parameters
            Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

            const { data } = await api.get('/v1/api/campaigns', { params });


            setCampaigns(data.content || []);
            setPagination({
                page: data.page.number || 0,
                size: data.page.size || 10,
                totalElements: data.page.totalElements || 0,
                totalPages: data.page.totalPages || 0
            });
        } catch (error) {
            console.error('Error fetching campaigns:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, [statusFilter, pagination.page, pagination.size, appliedSearch, dateRange, filters]);

    const handleStatusChange = (event) => {
        setStatusFilter(event.target.value);
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage - 1 }));
    };
    const handleSizeChange = (event) => {
        setPagination(prev => ({
            ...prev,
            size: parseInt(event.target.value, 10),
            page: 0
        }));
    };

    const handleMenuOpen = (event, campaign) => {
        setAnchorEl(event.currentTarget);
        setSelectedCampaign(campaign);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleVerifyClick = () => {
        setVerifyModalOpen(true);
        handleMenuClose();
    };

    const handleSuspendClick = (suspend) => {
        setIsSuspending(suspend);
        setSuspendModalOpen(true);
        handleMenuClose();
    };

    // Update the action success handler
    const handleActionSuccess = (message) => {
        setActionSuccess(`Campaign ${message} successfully`);
        fetchCampaigns();
    };

    const handleVerifyCampaign = async (approve) => {
        if (!selectedCampaign) return;

        try {
            await api.put(`/v1/api/admin/campaigns/${selectedCampaign.id}/verify`, {
                approved: approve
            });
            setActionSuccess(`Campaign ${approve ? 'approved' : 'rejected'} successfully`);
            fetchCampaigns();
        } catch (error) {
            console.error('Error verifying campaign:', error);
            setActionSuccess(null);
        } finally {
            handleMenuClose();
        }
    };

    // const handleSuspendCampaign = async (suspend) => {
    //     if (!selectedCampaign) return;

    //     try {
    //         await api.put(`/v1/api/campaigns/${selectedCampaign.id}/suspend`, {
    //            // message: 
    //         });
    //         setActionSuccess(`Campaign ${suspend ? 'suspended' : 'unsuspended'} successfully`);
    //         fetchCampaigns();
    //     } catch (error) {
    //         console.error('Error suspending campaign:', error);
    //         setActionSuccess(null);
    //     } finally {
    //         handleMenuClose();
    //     }
    // };

    const handleActivateCampaign = async (suspend) => {
        if (!selectedCampaign) return;

        try {
            await api.put(`/v1/api/campaigns/${selectedCampaign.id}/activate`, {
            });
            setActionSuccess(`Campaign ${suspend ? 'suspended' : 'unsuspended'} successfully`);
            fetchCampaigns();
        } catch (error) {
            console.error('Error suspending campaign:', error);
            setActionSuccess(null);
        } finally {
            handleMenuClose();
        }
    };

    const handleSearchSubmit = () => {
        setAppliedSearch({ term: searchTerm.trim(), by: searchBy });
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    const handleDateChange = (date, type) => {
        setDateRange(prev => ({ ...prev, [type]: date }));
    };

    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    const handleFilterChange = (e) => {
        const { name, value, checked } = e.target;
        if (name === 'categories') {
            setFilters(prev => ({
                ...prev,
                categories: checked
                    ? [...prev.categories, value]
                    : prev.categories.filter(c => c !== value)
            }));
        } else if (name === 'general') {
            setFilters(prev => ({
                ...prev,
                general: checked
                    ? [...prev.general, value]
                    : prev.general.filter(c => c !== value)
            }));
        } else {
            setFilters(prev => ({ ...prev, [name]: value }));
        }
    };

    const resetFilters = () => {
        setFilters({
            categories: [],
            general: [],
            minAmount: '',
            maxAmount: ''
        });
        setDateRange({
            start: subMonths(new Date(), 1),
            end: new Date()
        });
        setSearchTerm('');
        setAppliedSearch({ term: '', by: 'title' });
        setSearchBy('title');
        handleFilterClose();
    };

    const getStatusChip = (status) => {

        const statusConfig = {
            PENDING_VERIFICATION: { label: 'Pending Verification', color: 'warning', icon: <HourglassEmpty /> },
            ACTIVE: { label: 'Active', color: 'success', icon: <CheckCircle /> },
            PENDING: { label: 'Pending', color: 'primary', icon: <HourglassEmpty /> },
            VERIFIED: { label: 'Verified', color: 'success', icon: <CheckCircle /> },
            SUSPENDED: { label: 'Suspended', color: 'error', icon: <Block /> },
            FLAGGED: { label: 'Flagged', color: 'warning', icon: <Block /> },
            DELETED: { label: 'Deleted', color: 'error', icon: <Block /> },
            COMPLETED: { label: 'Completed', color: 'info', icon: <VerifiedUser /> }
        };

        const config = statusConfig[status] || { label: status, color: 'default' };
        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                icon={config.icon}
                sx={{ fontWeight: 'bold' }}
            />
        );
    };

    const getVerificationStatusChip = (requiresVerification = false, isVerfied = false) => {

        let status = 'N/A'
        if (requiresVerification && isVerfied) {
            status = 'VERIFIED'
        } else if (requiresVerification && isVerfied == false) {
            status = 'PENDING_VERIFICATION'
        }
        const statusConfig = {
            PENDING_VERIFICATION: { label: 'Pending Verification', color: 'warning', icon: <HourglassEmpty /> },
            VERIFIED: { label: 'Verified', color: 'success', icon: <CheckCircle /> },
        };


        const config = statusConfig[status] || { label: status, color: 'default' };
        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                icon={config.icon}
                sx={{ fontWeight: 'bold' }}
            />
        );
    };

    const getImageUrl = (campaign) => campaign.coverImagePath
        ? `${api.defaults.baseURL}/v1/api/campaigns/${campaign.id}/cover-image`
        : 'https://source.unsplash.com/random/300x200/?campaign';

    const filterOpen = Boolean(filterAnchorEl);
    const filterId = filterOpen ? 'filter-popover' : undefined;

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                    Campaign Administration
                </Typography>

                {actionSuccess && (
                    <Alert severity="success" onClose={() => setActionSuccess(null)} sx={{ mb: 2 }}>
                        {actionSuccess}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                        <DatePicker
                            label="Start Date"
                            value={dateRange.start}
                            onChange={(date) => handleDateChange(date, 'start')}
                            renderInput={(params) => (
                                <TextField {...params} size="small" sx={{ minWidth: 180 }} />
                            )}
                        />
                        <DatePicker
                            label="End Date"
                            value={dateRange.end}
                            onChange={(date) => handleDateChange(date, 'end')}
                            renderInput={(params) => (
                                <TextField {...params} size="small" sx={{ minWidth: 180 }} />
                            )}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
                        {/* Unified search control */}
                        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                            <FormControl
                                size="small"
                                sx={{
                                    minWidth: 180,
                                    mr: 0,
                                    '& .MuiOutlinedInput-root': {
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0
                                    }
                                }}
                            >
                                <InputLabel id="search-by-label">Search by</InputLabel>
                                <Select
                                    labelId="search-by-label"
                                    id="search-by"
                                    value={searchBy}
                                    label="Search by"
                                    onChange={(e) => setSearchBy(e.target.value)}
                                >
                                    <MenuItem value="title">Title</MenuItem>
                                    <MenuItem value="createdBy">Creator</MenuItem>
                                    <MenuItem value="id">Campaign ID</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                placeholder={`Search by ${searchBy === 'createdBy' ? 'Creator' : searchBy === 'id' ? 'Campaign ID' : 'Title'}...`}
                                variant="outlined"
                                size="small"
                                sx={{
                                    minWidth: 280,
                                    ml: '-1px',
                                    mr: 0,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 0
                                    }
                                }}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearchSubmit();
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleSearchSubmit}
                                disabled={!searchTerm.trim()}
                                sx={{
                                    height: '40px',
                                    ml: '-1px',
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Search
                            </Button>
                        </Box>

                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="status-filter-label">Status</InputLabel>
                            <Select
                                labelId="status-filter-label"
                                id="status-filter"
                                value={statusFilter}
                                label="Status"
                                onChange={handleStatusChange}
                            >
                                {statusOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={handleFilterClick}
                            aria-describedby={filterId}
                            sx={{ height: 40 }}
                        >
                            Filters
                        </Button>
                    </Box>
                </Box>

                {/* Filter Popover */}
                <Popover
                    id={filterId}
                    open={filterOpen}
                    anchorEl={filterAnchorEl}
                    onClose={handleFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    sx={{ mt: 1 }}
                >
                    <Box sx={{ p: 3, width: 300 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Filter Campaigns
                        </Typography>

                        <FormControl component="fieldset" sx={{ mb: 2 }} fullWidth>
                            <Typography variant="subtitle2">General</Typography>
                            <FormGroup>
                                {['Verified', 'Pending Verification', 'Requires Verification'].map(gen => (
                                    <FormControlLabel
                                        key={gen}
                                        control={
                                            <Checkbox
                                                checked={filters.general.includes(gen)}
                                                onChange={handleFilterChange}
                                                name="general"
                                                value={gen}
                                            />
                                        }
                                        label={gen}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>

                        <FormControl component="fieldset" sx={{ mb: 2 }} fullWidth>
                            <Typography variant="subtitle2">Categories</Typography>
                            <FormGroup>
                                {['Medical', 'Education', 'Emergency', 'Non-Profit'].map(category => (
                                    <FormControlLabel
                                        key={category}
                                        control={
                                            <Checkbox
                                                checked={filters.categories.includes(category)}
                                                onChange={handleFilterChange}
                                                name="categories"
                                                value={category}
                                            />
                                        }
                                        label={category}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>

                        <Typography variant="subtitle2" gutterBottom>
                            Amount Range
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                            <TextField
                                label="Min Amount"
                                type="number"
                                size="small"
                                name="minAmount"
                                value={filters.minAmount}
                                onChange={handleFilterChange}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                fullWidth
                            />
                            <TextField
                                label="Max Amount"
                                type="number"
                                size="small"
                                name="maxAmount"
                                value={filters.maxAmount}
                                onChange={handleFilterChange}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                fullWidth
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button onClick={resetFilters} color="error">
                                Reset All
                            </Button>
                            <Button
                                onClick={handleFilterClose}
                                variant="contained"
                                color="primary"
                            >
                                Apply Filters
                            </Button>
                        </Box>
                    </Box>
                </Popover>

                {/* Status Tabs replaced with Status Select above */}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : campaigns.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">
                            No campaigns found matching your criteria
                        </Typography>
                        <Button
                            variant="outlined"
                            sx={{ mt: 2 }}
                            onClick={() => {
                                resetFilters();
                            }}
                        >
                            Clear all filters
                        </Button>
                    </Paper>
                ) : (
                    <>
                        <TableContainer component={Paper} sx={{ maxHeight: '65vh' }}>
                            <Table size="small" stickyHeader sx={{
                                '& .MuiTableCell-root': { py: 0.5, fontSize: '0.82rem' },
                                '& .MuiTableCell-head': { py: 0.5, fontSize: '0.8rem', fontWeight: 600, letterSpacing: 0.1 },
                                '& tbody tr:hover': { bgcolor: 'action.hover' },
                                '& tbody tr:nth-of-type(odd)': { bgcolor: 'background.default' }
                            }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ py: 0.5 }}>Campaign</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Created</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Creator</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Status</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Requires Verification</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Verification Status</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Amount Raised</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Donors</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Due Date</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {campaigns.map((campaign) => (
                                        <TableRow key={campaign.id} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Avatar
                                                        variant="rounded"
                                                        src={getImageUrl(campaign)}
                                                        sx={{ width: 40, height: 40, mr: 2 }}
                                                    />
                                                    <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 360 }}>
                                                        {campaign.title}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {/* {format(new Date(campaign.createdAt), 'MMM dd, yyyy')} */}
                                                    {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {campaign.createdBy}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{getStatusChip(campaign.campaignStatus)}</TableCell>
                                            <TableCell>
                                                <Chip label={campaign.requiresVerification ? 'Yes' : 'No'} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell>{getVerificationStatusChip(campaign.requiresVerification, campaign.verified)}</TableCell>
                                            <TableCell>
                                                <Typography fontWeight={600} variant="body2" color="success.main">
                                                    ${campaign.amountRaised?.toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption" color="primary.main" >
                                                    of ${campaign.goalAmount?.toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{campaign.daysLeft}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {campaign.dueDate}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={(e) => handleMenuOpen(e, campaign)} size="small" sx={{ p: 0.5 }}>
                                                    <MoreVert />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={pagination.totalElements}
                            rowsPerPage={pagination.size}
                            page={pagination.page}
                            onPageChange={(event, newPage) => {
                                setPagination(prev => ({ ...prev, page: newPage }));
                            }}
                            onRowsPerPageChange={handleSizeChange}
                            sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
                        />

                    </>
                )}

                {/* Action Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                >
                    <MenuItem onClick={() => {
                        setPreviewModalOpen(true);
                        handleMenuClose();
                    }}>
                        <Visibility sx={{ mr: 1 }} /> Preview Campaign
                    </MenuItem>
                    {selectedCampaign?.requiresVerification && selectedCampaign.verified == false && (
                        <MenuItem onClick={handleVerifyClick}>
                            <Gavel sx={{ mr: 1 }} /> Verify Campaign
                        </MenuItem>
                    )}
                    {selectedCampaign?.campaignStatus !== 'SUSPENDED' && selectedCampaign?.campaignStatus !== 'FLAGGED' && (
                        <MenuItem onClick={() => handleSuspendClick(true)}>
                            <Block color="warning" sx={{ mr: 1 }} /> Suspend Campaign
                        </MenuItem>
                    )}
                    {(selectedCampaign?.campaignStatus === 'SUSPENDED'  || selectedCampaign?.campaignStatus === 'FLAGGED') && (
                        <MenuItem onClick={() => handleActivateCampaign(false)}>
                            <CheckCircle color="success" sx={{ mr: 1 }} /> Activate Campaign
                        </MenuItem>
                    )}
                    <MenuItem
                        onClick={() => {
                            setPayoutModalOpen(true);
                            handleMenuClose();
                        }}
                        disabled={selectedCampaign?.amountRaised <= 0}
                    >
                        <Payment color="primary" sx={{ mr: 1 }} /> Process Payout
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setRefundModalOpen(true);
                            handleMenuClose();
                        }}
                        disabled={selectedCampaign?.amountRaised <= 0}
                    >
                        <MoneyOff color="error" sx={{ mr: 1 }} /> Refund Donations
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleMenuClose}>
                        <Refresh sx={{ mr: 1 }} /> Refresh Data
                    </MenuItem>
                </Menu>

                {/* Modals */}
                <PreviewCampaignModal
                    open={previewModalOpen}
                    onClose={() => setPreviewModalOpen(false)}
                    campaign={selectedCampaign}
                />

                <PayoutModal
                    open={payoutModalOpen}
                    onClose={() => setPayoutModalOpen(false)}
                    campaign={selectedCampaign}
                    onSuccess={() => {
                        setActionSuccess('Payout processed successfully');
                        fetchCampaigns();
                    }}
                />

                <RefundModal
                    open={refundModalOpen}
                    onClose={() => setRefundModalOpen(false)}
                    campaign={selectedCampaign}
                    onSuccess={() => {
                        setActionSuccess('Refunds processed successfully');
                        fetchCampaigns();
                    }}
                />

                <VerifyCampaignModal
                    open={verifyModalOpen}
                    onClose={() => setVerifyModalOpen(false)}
                    campaign={selectedCampaign}
                    onSuccess={(action) => handleActionSuccess(action)}
                />

                <SuspendCampaignModal
                    open={suspendModalOpen}
                    onClose={() => setSuspendModalOpen(false)}
                    campaign={selectedCampaign}
                    onSuccess={(action) => handleActionSuccess(action)}
                    isSuspending={isSuspending}
                />
            </Box>
        </LocalizationProvider>
    );
};

export default AdminCampaignDashboard;